import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, FileText, Receipt, Upload, 
  LogOut, Plane, Clock, CheckCircle, AlertCircle, ClipboardCheck, Download 
} from 'lucide-react';
import { logger } from '../../utils/logger';
import { UploadDocumentModal } from '@/components/UploadDocumentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  'quote_sent': 'Quote Sent',
  'booking_confirmed': 'Booking Confirmed',
  'documents_required': 'Documents Required',
  'documents_submitted': 'Documents Submitted',
  'documents_under_review': 'Under Review',
  'documents_approved': 'Documents Approved',
  'travel_arrangements': 'Travel Arrangements',
  'flight_booked': 'Flight Booked',
  'pre_travel_prep': 'Pre-Travel Prep',
  'in_transit': 'In Transit',
  'delivered': 'Delivered',
  'complete': 'Complete'
};

const STATUS_COLORS = {
  'quote_sent': 'bg-gray-100 text-gray-800',
  'booking_confirmed': 'bg-blue-100 text-blue-800',
  'documents_required': 'bg-yellow-100 text-yellow-800',
  'documents_submitted': 'bg-purple-100 text-purple-800',
  'documents_under_review': 'bg-orange-100 text-orange-800',
  'documents_approved': 'bg-green-100 text-green-800',
  'travel_arrangements': 'bg-indigo-100 text-indigo-800',
  'flight_booked': 'bg-cyan-100 text-cyan-800',
  'pre_travel_prep': 'bg-teal-100 text-teal-800',
  'in_transit': 'bg-pink-100 text-pink-800',
  'delivered': 'bg-green-100 text-green-800',
  'complete': 'bg-gray-100 text-gray-800'
};

export const ClientDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [uploadModal, setUploadModal] = useState(null);
  const [readinessReports, setReadinessReports] = useState([]);
  const clientId = sessionStorage.getItem('client_id');
  const clientName = sessionStorage.getItem('client_name');

  useEffect(() => {
    if (!clientId) {
      navigate('/client/login');
      return;
    }
    fetchDashboardData();
    fetchReadinessReports();
  }, [clientId]);

  const fetchReadinessReports = async () => {
    try {
      const response = await fetch(`${API_URL}/api/client/${clientId}/readiness-reports`);
      if (response.ok) {
        const reports = await response.json();
        setReadinessReports(reports);
      }
    } catch (error) {
      logger.error('Error fetching readiness reports:', error);
    }
  };

  const downloadReport = async (reportId, filename) => {
    try {
      const response = await fetch(`${API_URL}/api/client/readiness-reports/${reportId}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      logger.error('Error downloading report:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/client/${clientId}/dashboard`);
      if (response.ok) {
        const dashboardData = await response.json();
        setData(dashboardData);
      }
    } catch (err) {
      logger.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/client/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const activeRelocations = data?.relocations?.filter(r => r.current_status !== 'complete') || [];
  const pastRelocations = data?.relocations?.filter(r => r.current_status === 'complete') || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Welcome back, {clientName}!</h1>
              <p className="text-sm text-gray-600">Manage your pet relocations</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Active Relocations</p>
                <p className="text-3xl font-bold text-gray-900">{activeRelocations.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Plane className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Quotes</p>
                <p className="text-3xl font-bold text-gray-900">{data?.quotes?.length || 0}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Documents</p>
                <p className="text-3xl font-bold text-gray-900">{data?.documents?.length || 0}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Upload className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Completed Trips</p>
                <p className="text-3xl font-bold text-gray-900">{pastRelocations.length}</p>
              </div>
              <div className="bg-gray-100 p-3 rounded-lg">
                <CheckCircle className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Active Relocations */}
        {activeRelocations.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Active Relocations</h2>
            <div className="grid grid-cols-1 gap-4">
              {activeRelocations.map(relocation => (
                <div key={relocation.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{relocation.pet_name}</h3>
                      <p className="text-sm text-gray-600">{relocation.pet_type} • {relocation.pet_breed || 'Mixed Breed'}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[relocation.current_status]}`}>
                      {STATUS_LABELS[relocation.current_status]}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Origin</p>
                      <p className="text-sm font-medium text-gray-900">{relocation.origin}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Destination</p>
                      <p className="text-sm font-medium text-gray-900">{relocation.destination}</p>
                    </div>
                  </div>

                  {relocation.travel_date && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <Clock className="w-4 h-4" />
                      Travel Date: {new Date(relocation.travel_date).toLocaleDateString()}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate(`/client/relocations/${relocation.id}`)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View Details →
                    </button>
                    <button
                      onClick={() => setUploadModal(relocation)}
                      className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-medium"
                    >
                      <Upload className="w-4 h-4" />
                      Upload Documents
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Modal */}
        {uploadModal && (
          <UploadDocumentModal
            relocation={uploadModal}
            clientId={clientId}
            onClose={() => setUploadModal(null)}
            onSuccess={fetchDashboardData}
          />
        )}

        {/* Past Relocations */}
        {pastRelocations.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Travel History</h2>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pet</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travel Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pastRelocations.map(relocation => (
                    <tr key={relocation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{relocation.pet_name}</p>
                          <p className="text-sm text-gray-600">{relocation.pet_type}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {relocation.origin} → {relocation.destination}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {relocation.travel_date ? new Date(relocation.travel_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          Complete
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => navigate(`/client/relocations/${relocation.id}`)}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TravelSafe Readiness Reports Section */}
        {readinessReports.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <ClipboardCheck className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">TravelSafe Readiness Reports</h2>
            </div>

            <div className="space-y-4">
              {readinessReports.map((report) => (
                <div key={report.id} className="border border-gray-200 rounded-lg p-4 hover:border-green-500 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">{report.pet_name}</h3>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          report.readiness_status === 'Ready' ? 'bg-green-100 text-green-800' :
                          report.readiness_status?.includes('Attention') ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {report.readiness_status === 'Ready' && <CheckCircle className="w-4 h-4 inline mr-1" />}
                          {report.readiness_status?.includes('Attention') && <AlertCircle className="w-4 h-4 inline mr-1" />}
                          {report.readiness_status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Destination: {report.destination_country || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Generated: {new Date(report.generated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => downloadReport(report.id, report.filename)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Relocations */}
        {activeRelocations.length === 0 && pastRelocations.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Relocations Yet</h3>
            <p className="text-gray-600 mb-6">Your pet relocation bookings will appear here</p>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Request a Quote
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
